import { type FC, useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import { useAuth0 } from '@auth0/auth0-react';
import { debounce } from 'lodash';
import {
  ClearIndicator,
  CustomInput,
  CustomMultiValueRemove,
  DropdownIndicator,
} from '@/components/ui/react-select-elements';
import { shareDialogSelectClassNames } from '@/components/shares/share-dialog-select-class-names';
import { useAutocompleteTags, useMoveTagMutation } from '@/hooks/tags/useTags';
import { type Option } from '@/types/select-option';
import { useTranslations } from 'next-intl';

export type TagSelectorSaveProps =
  | {
      /**
       * Selecting a parent immediately calls the move endpoint, matching every other usage.
       * `onChange` is still allowed, to observe the change alongside the auto-save.
       */
      autoSave?: true;
      onChange?: (option: Option | null) => void;
    }
  | {
      /** Selecting an option only reports the change via `onChange` — the caller is
       * responsible for persisting it (e.g. a form's own Save button). */
      autoSave: false;
      onChange: (option: Option | null) => void;
    };

type TagSelectorProps = {
  tagId: string;
  parentTag: { id: string; name: string } | null;
  /** When true, only root (top-level) tags are offered as parent options. Defaults to false. */
  rootOnly?: boolean;
} & TagSelectorSaveProps;

export const TagSelector: FC<TagSelectorProps> = ({
  tagId,
  parentTag,
  autoSave = true,
  onChange,
  rootOnly = false,
}) => {
  const tCommon = useTranslations('common');
  const { isAuthenticated } = useAuth0();

  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<Option | null>(null);
  // `#portal` is a sibling rendered elsewhere in the tree, so it isn't in the real DOM
  // yet on this component's first render (e.g. when it mounts as part of the initial
  // page render, before that sibling commits). Query for it after mount instead, or
  // `menuPortalTarget` stays null and the menu falls back to rendering inline, pushing
  // the surrounding layout instead of floating over it.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setPortalTarget(document.querySelector<HTMLElement>('#portal'));
  }, []);

  useEffect(() => {
    setSelectedValue(parentTag ? { label: parentTag.name, value: parentTag.id } : null);
  }, [parentTag]);

  const { moveTagMutation } = useMoveTagMutation();

  const handleChange = (newOption: Option | null) => {
    if (autoSave) {
      moveTagMutation.mutate({
        move: [
          {
            parentTagId: newOption?.value ?? null,
            tags: [tagId],
          },
        ],
      });
    }

    setSelectedValue(newOption);
    onChange?.(newOption);
  };

  const debounceInputChange = useCallback(
    debounce((value) => {
      setInputValue(value);
    }, 300),
    [],
  );

  const handleInputChange = (newValue: string) => {
    if (newValue.length >= 1) {
      debounceInputChange(newValue);
    } else {
      setInputValue('');
    }
  };

  const { tags, areTagsFetching } = useAutocompleteTags(
    { q: inputValue, rootOnly },
    { enabled: Boolean(inputValue) && inputValue.length >= 1 && isAuthenticated },
  );

  return (
    <Select
      unstyled
      isClearable
      value={selectedValue}
      isMulti={false}
      options={(tags?.suggestions ?? []).map((tag) => ({ label: tag.name, value: tag.id }))}
      onChange={handleChange}
      onInputChange={handleInputChange}
      isLoading={areTagsFetching}
      isOptionDisabled={(option) => option.value === tagId}
      menuPortalTarget={portalTarget}
      menuPosition="fixed"
      menuPlacement="auto"
      components={{
        Input: CustomInput,
        DropdownIndicator,
        ClearIndicator,
        MultiValueRemove: CustomMultiValueRemove,
      }}
      noOptionsMessage={() => tCommon('noOptionsFound')}
      classNames={shareDialogSelectClassNames as any}
    />
  );
};
